<template>
  <div
    class="d-flex flex-column gap-4 flex-grow-1 flex-shrink-1"
    data-testid="id-verification-container">
    <loading-overlay
      v-if="isLoading || isIframeLoading"
      :show="true"
      :message="isLoading ? t('idVerification.processing') : t('idVerification.loadingIframe')" />

    <div v-if="verificationError" class="id-verification__error">
      <v-alert type="warning" icon="mdi-alert-circle-outline" prominent>
        <div class="d-flex flex-column gap-2 comet-i18n-exclude">
          <span class="typography-body-md-bold">{{ t('idVerification.issueTitle') }}</span>
          <span class="typography-body-sm">{{ verificationError }}</span>
        </div>
      </v-alert>
    </div>

    <div class="checkin__form-container d-flex flex-column gap-4 flex-grow-1 flex-shrink-1">
      <div class="id-verification__persona-container flex-grow-1 flex-shrink-1">
        <Inquiry
          v-if="templateId && personaEnvironmentId"
          :template-id="templateId"
          :environment-id="personaEnvironmentId"
          @ready="handleReady"
          @complete="handleComplete"
          @error="handleError"
          @cancel="handleCancel" />
        <v-alert v-else type="error" icon="mdi-alert-circle-outline" class="comet-i18n-exclude">
          {{ t('idVerification.notConfigured') }}
        </v-alert>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, type PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import { Inquiry } from 'persona-vue';
import { isFeatureEnabledForWarehouse, WarehouseFeatureEnum, type IWarehouse } from '@nova/core';
import { personaEnvironmentId } from '@/config';
import LoadingOverlay from './LoadingOverlay.vue';

interface PersonaCompleteEvent {
  inquiryId: string;
  status: string;
  fields: Record<string, unknown>;
}

interface PersonaErrorEvent {
  status: string;
  code?: string;
}

export default defineComponent({
  name: 'IdVerification',
  components: {
    Inquiry,
    LoadingOverlay
  },
  props: {
    warehouse: {
      type: Object as PropType<IWarehouse>,
      required: true
    }
  },
  emits: ['complete', 'error', 'skip'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const isLoading = ref(false);
    const isIframeLoading = ref(true);
    const isReady = ref(false);
    const verificationError = ref<string | null>(null);
    const hasCompletedVerification = ref(false);

    const templateId = computed(() => {
      return props.warehouse.settings?.checkinIdValidationTemplateId as string | undefined;
    });

    const hasIdValidationFeature = computed(() => {
      return isFeatureEnabledForWarehouse(props.warehouse, WarehouseFeatureEnum.IdValidation);
    });

    const isValidationRequired = computed(() => {
      return (
        hasIdValidationFeature.value &&
        props.warehouse.settings?.checkinIdValidationRequired === true
      );
    });

    const isEnabled = computed(() => {
      return (
        hasIdValidationFeature.value && props.warehouse.settings?.enableCheckinIdValidation === true
      );
    });

    const handleReady = () => {
      isIframeLoading.value = false;
      isReady.value = true;
    };

    const handleComplete = async (event: PersonaCompleteEvent) => {
      if (hasCompletedVerification.value) {
        return;
      }

      const { inquiryId, status } = event;
      isLoading.value = true;
      verificationError.value = null;
      hasCompletedVerification.value = true;

      try {
        if (['completed', 'approved'].includes(status)) {
          emit('complete', { inquiryId, status, success: true });
        } else {
          if (isValidationRequired.value) {
            verificationError.value = t('idVerification.notSuccessful');
            emit('error', { inquiryId, status, message: verificationError.value });
          } else {
            verificationError.value = t('idVerification.couldNotCompleteProceed');
            emit('complete', { inquiryId, status, success: false });
          }
        }
      } catch (error: unknown) {
        hasCompletedVerification.value = false;

        console.error('Failed to complete ID verification:', error);

        if (isValidationRequired.value) {
          verificationError.value = t('idVerification.processFailed');
          emit('error', { inquiryId, status: 'failed', message: verificationError.value });
        } else {
          verificationError.value = t('idVerification.processingFailedProceed');
          emit('complete', { inquiryId, status: 'failed', success: false });
        }
      } finally {
        isLoading.value = false;
      }
    };

    const handleError = (event: PersonaErrorEvent) => {
      console.error('Persona verification error:', event);

      if (isValidationRequired.value) {
        verificationError.value = t('idVerification.genericError');
        emit('error', { status: event.status, code: event.code });
      } else {
        verificationError.value = t('idVerification.issueProceed');
        emit('skip', { reason: 'error', event });
      }
    };

    const handleCancel = () => {
      if (isValidationRequired.value) {
        verificationError.value = t('idVerification.requiredTryAgain');
      } else {
        emit('skip', { reason: 'cancelled' });
      }
    };

    return {
      t,
      isLoading,
      isIframeLoading,
      verificationError,
      templateId,
      isEnabled,
      isReady,
      isValidationRequired,
      personaEnvironmentId,
      handleReady,
      handleComplete,
      handleError,
      handleCancel
    };
  }
});
</script>

<style lang="scss" scoped>
.id-verification {
  display: flex;
  flex-direction: column;
  gap: $spacing-6;
}

.id-verification__error {
  margin-bottom: $spacing-4;
}

.id-verification__persona-container > iframe {
  min-height: 100%;
  width: 100%;
}
</style>
